/** @jsxImportSource @emotion/react */
import { Container, Link, List, Message, Stack, Text } from "basis";
import { Separator } from "components/Separator/Separator";
import { Switch } from "components/Switch";
import { formatMaskedValue } from "library/utils";
import { NotificationPreferencesMutationKey } from "pages/Notifications/types";
import { useNotificationPreferences } from "../hooks";
import { ExceptionContent } from "./ExceptionContent";

type Props = {
  token: string;
  accountId: string;
};

export const PaymentNotification = ({ token, accountId }: Props) => {
  const {
    switchLoading,
    switchError,
    updatePreferences,
    getPreferences,
    setSwitchLoading,
  } = useNotificationPreferences(token);
  const { isLoading, isError, data } = getPreferences;
  const { isLoading: updateInProgress, mutate } = updatePreferences;
  const isReadOnly = Boolean(data?.preferences.readOnly);
  const isReadOnlyOrDisabled = isReadOnly || updateInProgress;

  function handleChange(
    value: boolean,
    key: NotificationPreferencesMutationKey,
  ) {
    if (!isReadOnlyOrDisabled && data !== undefined) {
      setSwitchLoading(key);
      mutate({
        token: token!,
        payload: { [key]: value },
        state: data.preferences,
      });
    }
  }

  const UpdateErrorMessage = (
    <Message
      severity="blocking"
      bg="secondary.pink.t30"
      testId="update-payment-notification-response-error"
    >
      Sorry, we are experiencing difficulties updating the notifications for
      your account. Please try again or contact Latitude if the problem
      persists.
    </Message>
  );

  if (isLoading) {
    return null;
  }

  if (isError) {
    return <ExceptionContent />;
  }

  return (
    <Container padding="2 0">
      <Stack gap="8">
        <Stack gap="8" testId="payment-notification">
          <Text as="h3" textStyle="heading5" color="primary.blue.t100">
            Payment reminders and notifications
          </Text>
          <Text>
            <strong>Sent to:</strong>
            <br />
            <span
              css={{
                textTransform: "lowercase",
              }}
            >
              {formatMaskedValue(data?.personalDetails?.email ?? "")}
            </span>
          </Text>
        </Stack>
        <Stack gap="5" testId="payment-notification">
          <Text>
            We can send you reminders and notifications by email, helping you to
            stay on top of your account.
          </Text>
          <List>
            <List.Item>
              Customers are responsible for managing their notification
              preferences.
            </List.Item>
            <List.Item>
              You are still required to make regular payments, even if these
              notifications are switched off.
            </List.Item>
          </List>
        </Stack>
        <Separator />
        <Stack gap="5" testId="payment-due-notification">
          {isReadOnly && (
            <Message severity="info-or-minor" bg="secondary.pink.t30">
              We're unable to show your notification preferences via the
              Latitude Service Centre. <br /> Please{" "}
              <Link href="/contact" newTab={false}>
                Contact us
              </Link>
              .
            </Message>
          )}
          <Switch
            id={`toggle-email-before-payment-due-${accountId}`}
            checked={Boolean(data?.preferences?.emailBeforePaymentDue)}
            onChange={(value) => handleChange(value, "emailBeforePaymentDue")}
            loading={switchLoading === "emailBeforePaymentDue"}
            disabled={isReadOnlyOrDisabled}
            labelChildren={
              <Text>
                <strong>Payment due</strong>
                <br />
                <span>
                  Reminder sent 7 days before minimum monthly payment is due.
                </span>
              </Text>
            }
          />
          {switchError === "emailBeforePaymentDue" && UpdateErrorMessage}
        </Stack>
        <Separator />
        <Stack gap="5" testId="payment-received-notification">
          <Switch
            id={`toggle-email-after-payment-received-${accountId}`}
            checked={Boolean(data?.preferences?.emailAfterPaymentReceived)}
            loading={switchLoading === "emailAfterPaymentReceived"}
            disabled={isReadOnlyOrDisabled}
            onChange={(value) =>
              handleChange(value, "emailAfterPaymentReceived")
            }
            labelChildren={
              <Text>
                <strong>Payment received</strong>
                <br />
                <span>Notification sent when a payment is received.</span>
              </Text>
            }
          />
          {switchError === "emailAfterPaymentReceived" && UpdateErrorMessage}
        </Stack>
      </Stack>
    </Container>
  );
};
